import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { authQueryOptions, useAuthQuery } from '../api/auth/queries/auth-query'
import type { ResAuth } from '@/shared/types'
import { router } from '@/app/router/router'
import { getAccessToken, getRefreshToken } from '@/shared/lib/auth-tokens'
import { refreshAuthTokens } from '@/shared/lib/refresh-auth-tokens'
import { useAuthTokensRevision } from './useAuthTokensRevision'

type AuthState =
  | { user: null; status: 'PENDING' }
  | { user: null; status: 'UNAUTHENTICATED' }
  | { user: ResAuth['user']; status: 'AUTHENTICATED' }

type AuthUtils = {
  ensureData: () => Promise<ResAuth | null>
}

type AuthData = AuthState & AuthUtils

function useAuth(): AuthData {
  const authQuery = useAuthQuery()
  const tokensRevision = useAuthTokensRevision()
  const hasAccessToken = Boolean(getAccessToken())
  const hasRefreshToken = Boolean(getRefreshToken())
  const [isRefreshing, setIsRefreshing] = useState(false)

  // tokensRevision forces re-read of cookies after cross-tab auth changes.
  void tokensRevision

  const queryClient = useQueryClient()

  useEffect(() => {
    router.invalidate()
  }, [authQuery.data, tokensRevision])

  useEffect(() => {
    if (authQuery.error === null) return
    queryClient.setQueryData(['auth'], null)
  }, [authQuery.error, queryClient])

  useEffect(() => {
    if (hasAccessToken) return

    // Access token gone but refresh token present — recover silently. Fires
    // proactively (even before authQuery resolves) so the session is restored
    // without a 401 round-trip flashing an unauthenticated frame first.
    if (hasRefreshToken) {
      let cancelled = false
      setIsRefreshing(true)

      refreshAuthTokens()
        .catch(() => {
          if (!cancelled) {
            queryClient.setQueryData(['auth'], null)
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsRefreshing(false)
          }
        })

      return () => {
        cancelled = true
      }
    }

    // No way to recover — drop any cached session.
    if (authQuery.data) {
      queryClient.setQueryData(['auth'], null)
    }
  }, [authQuery.data, hasAccessToken, hasRefreshToken, queryClient])

  const utils: AuthUtils = {
    ensureData: () => {
      return queryClient.ensureQueryData(authQueryOptions())
    },
  }

  // Access token expired but a refresh token is still available: a refresh is
  // in flight (or about to be kicked off by the effect above). Treat it as a
  // transient state, never UNAUTHENTICATED — otherwise the route guard would
  // redirect to /login for one frame before the refresh completes (flicker).
  const canRecoverSession = !hasAccessToken && hasRefreshToken

  switch (true) {
    case authQuery.isPending:
      return { ...utils, user: null, status: 'PENDING' }

    case isRefreshing || canRecoverSession:
      if (authQuery.data) {
        return { ...utils, user: authQuery.data.user, status: 'AUTHENTICATED' }
      }
      return { ...utils, user: null, status: 'PENDING' }

    case !authQuery.data || !hasAccessToken:
      return { ...utils, user: null, status: 'UNAUTHENTICATED' }

    default:
      return { ...utils, user: authQuery.data.user, status: 'AUTHENTICATED' }
  }
}

export { useAuth }
export type { AuthData }
